import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { IconButton, Skeleton, type Theme, Typography } from "@mui/material";
import { Box, type SxProps } from "@mui/system";
import { DataGrid, type GridColDef, type GridColumnGroupingModel, type GridRenderCellParams } from "@mui/x-data-grid";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { useExpandedGroups } from "../../../hooks/useExpandedGroups";
import type { TranslationKeys } from "../../../i18n/types";
import { HOURS_SUMMARY } from "../../../theme/tokens";
import { MC_GRID_SX } from "../utils";
import type { HoursSummaryResponse, HoursSummaryRoleGroup, HoursSummaryShiftPattern } from "./types";
import { DAY_NAMES } from "./utils";

// Functional / Categorical: row banding tints. Must stay stable across themes.
const HOURS_TABLE_TINTS = {
	GROUP_HEADER_BG: "#E3F2FD",
	GROUP_HEADER_HOVER: "#D6EAFB",
	TOTAL_BG: "#F5F5F5",
	TOTAL_HOVER: "#EFEFEF",
	COL_HEADER_BG: "#F5F6FA",
} as const;

type CategoryKey = "needed" | "recurring" | "pickup" | "weeklyOt" | "consecutiveOt" | "dailyOt" | "shiftOverrun" | "agency" | "total";

type HoursSummaryRow = {
	id: string;
	category: string;
	categoryKey: CategoryKey;
	roleGroup: string;
	isGroupHeader: boolean;
	isExpandable: boolean;
	isExpanded: boolean;
	isPatternRow: boolean;
	isTotalRow: boolean;
	// Day values: d0..d13
	[key: `d${number}`]: number;
};

const CATEGORY_CONFIG = [
	{ key: "needed", labelKey: "hours.summary.needed.hours", expandable: false, color: HOURS_SUMMARY.NEEDED, actualOnly: false, consecutiveRuleOnly: false },
	{ key: "recurring", labelKey: "hours.summary.recurring.hours", expandable: false, color: undefined, actualOnly: false, consecutiveRuleOnly: false },
	{ key: "pickup", labelKey: "hours.summary.pickup.hours", expandable: false, color: undefined, actualOnly: false, consecutiveRuleOnly: false },
	{ key: "weeklyOt", labelKey: "hours.summary.weekly.ot", expandable: false, color: HOURS_SUMMARY.WEEKLY_OT, actualOnly: false, consecutiveRuleOnly: false },
	// DAT-289: shown only for departments that CONFIGURE a consecutive-day rule — none today — so the
	// other ~300 keep exactly the rows they have now. No pattern breakdown, matching Weekly OT.
	{
		key: "consecutiveOt",
		labelKey: "hours.summary.consecutive.ot",
		expandable: false,
		color: HOURS_SUMMARY.CONSECUTIVE_OT,
		actualOnly: false,
		consecutiveRuleOnly: true,
	},
	{ key: "dailyOt", labelKey: "hours.summary.daily.ot", expandable: true, color: HOURS_SUMMARY.DAILY_OT, actualOnly: false, consecutiveRuleOnly: false },
	{ key: "agency", labelKey: "hours.summary.agency.hours", expandable: true, color: HOURS_SUMMARY.AGENCY, actualOnly: false, consecutiveRuleOnly: false },
	{
		key: "shiftOverrun",
		labelKey: "hours.summary.shift.overrun",
		expandable: false,
		color: HOURS_SUMMARY.OVERRUN,
		actualOnly: true,
		consecutiveRuleOnly: false,
	},
	{ key: "total", labelKey: "hours.summary.total.hours", expandable: false, color: undefined, actualOnly: false, consecutiveRuleOnly: false },
] as const satisfies readonly {
	key: CategoryKey;
	labelKey: TranslationKeys;
	expandable: boolean;
	color: string | undefined;
	actualOnly: boolean;
	consecutiveRuleOnly: boolean;
}[];

/**
 * Which category rows this response gets. Exported so it can be tested directly — re-implementing
 * the rule in a spec would let the component drift while the test still passes.
 *
 * `shiftOverrun` is actuals-only; `consecutiveOt` (DAT-289) is shown only when the department
 * CONFIGURES a consecutive-day rule, never on whether hours happened to land in the visible range.
 */
export function visibleCategoryKeys(isActual: boolean, hasConsecutiveOtRule: boolean): CategoryKey[] {
	return CATEGORY_CONFIG.filter((cat) => !(cat.actualOnly && !isActual) && !(cat.consecutiveRuleOnly && !hasConsecutiveOtRule)).map((cat) => cat.key);
}

function formatShiftTime(timeStr: string): string {
	const [h, m] = timeStr.split(":");
	const hour = Number.parseInt(h, 10);
	const suffix = hour >= 12 ? "pm" : "am";
	const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
	return m === "00" ? `${displayHour}${suffix}` : `${displayHour}:${m}${suffix}`;
}

const OTHER_PATTERN_SENTINEL = "00:00:01";

function getPatternLabel(pattern: HoursSummaryShiftPattern, t: (key: string) => string): string {
	if (pattern.startTime === OTHER_PATTERN_SENTINEL) return t("hours.summary.other.patterns");
	return `${formatShiftTime(pattern.startTime)}-${formatShiftTime(pattern.endTime)}`;
}

function getDayValue(rg: HoursSummaryRoleGroup, dayIndex: number, categoryKey: CategoryKey): number {
	const day = rg.days[dayIndex];
	if (!day) {
		return 0;
	}

	switch (categoryKey) {
		case "needed": {
			return day.targetHours;
		}
		case "recurring": {
			return day.recurringHours;
		}
		case "pickup": {
			return day.pickupHours;
		}
		case "weeklyOt": {
			return day.weeklyOtHours;
		}
		case "consecutiveOt": {
			return day.consecutiveOtHours ?? 0;
		}
		case "dailyOt": {
			return day.dailyOtHours;
		}
		case "shiftOverrun": {
			return day.shiftOverrunHours;
		}
		case "agency": {
			return day.agencyHours;
		}
		case "total": {
			return day.totalHours;
		}
	}
}

function getCategoryColor(categoryKey: CategoryKey): string | undefined {
	return CATEGORY_CONFIG.find((c) => c.key === categoryKey)?.color;
}

const GRID_SX = {
	...MC_GRID_SX,
	maxHeight: "70vh",
	"& .MuiDataGrid-columnHeader": { p: 0 },
	"& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
	"& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainer": { fontWeight: 700, bgcolor: HOURS_TABLE_TINTS.COL_HEADER_BG },
	"& .MuiDataGrid-row.group-header-row": { bgcolor: HOURS_TABLE_TINTS.GROUP_HEADER_BG, "&:hover": { bgcolor: HOURS_TABLE_TINTS.GROUP_HEADER_HOVER } },
	"& .MuiDataGrid-row.total-row": { bgcolor: HOURS_TABLE_TINTS.TOTAL_BG, "&:hover": { bgcolor: HOURS_TABLE_TINTS.TOTAL_HOVER } },
	"& .MuiDataGrid-cell": { display: "flex", alignItems: "center", justifyContent: "center" },
	'& .MuiDataGrid-cell[data-field="category"]': { justifyContent: "flex-start" },
	'& [data-field="d6"]': { borderRight: "2px solid", borderColor: "grey.300" },
} as const satisfies SxProps<Theme>;

type Props = {
	data?: HoursSummaryResponse;
	isLoading: boolean;
	isActual: boolean;
};

export const HoursSummaryTable: FC<Props> = ({ data, isLoading, isActual }) => {
	const { t } = useTranslation();
	const { expandedGroups, toggleGroupExpand } = useExpandedGroups();

	const rows = useMemo(() => {
		if (!data?.roleGroups) return [];

		const result: HoursSummaryRow[] = [];
		for (const rg of data.roleGroups) {
			const groupId = `rg-${rg.roleGroup}`;
			const isGroupExpanded = expandedGroups.has(groupId);

			// Role group header row
			result.push({
				id: groupId,
				category: rg.roleGroup,
				categoryKey: "total",
				roleGroup: rg.roleGroup,
				isGroupHeader: true,
				isExpandable: true,
				isExpanded: isGroupExpanded,
				isPatternRow: false,
				isTotalRow: false,
				...Object.fromEntries(Array.from({ length: 14 }, (_, i) => [`d${i}`, 0])),
			} as HoursSummaryRow);

			// Category rows (only when group is expanded)
			if (!isGroupExpanded) {
				continue;
			}

			const visibleKeys = new Set(visibleCategoryKeys(isActual, data.hasConsecutiveOtRule ?? false));
			for (const cat of CATEGORY_CONFIG) {
				if (!visibleKeys.has(cat.key)) {
					continue;
				}

				// Hide needed row when all day values are 0 (non-ratio departments)
				if (cat.key === "needed") {
					const hasNeeded = Array.from({ length: 14 }, (_, i) => getDayValue(rg, i, cat.key)).some((v) => v > 0);
					if (!hasNeeded) {
						continue;
					}
				}
				const rowId = `${rg.roleGroup}-${cat.key}`;
				const dayValues = Object.fromEntries(Array.from({ length: 14 }, (_, i) => [`d${i}`, getDayValue(rg, i, cat.key)]));

				const isCatExpanded = cat.expandable && expandedGroups.has(rowId);
				result.push({
					id: rowId,
					category: t(cat.labelKey),
					categoryKey: cat.key,
					roleGroup: rg.roleGroup,
					isGroupHeader: false,
					isExpandable: cat.expandable,
					isExpanded: isCatExpanded,
					isPatternRow: false,
					isTotalRow: cat.key === "total",
					...dayValues,
				} as HoursSummaryRow);

				// If expanded, add pattern sub-rows
				if (isCatExpanded) {
					const patterns = getPatterns(rg, cat.key);
					for (const pattern of patterns) {
						const patternLabel = getPatternLabel(pattern, t);
						result.push({
							id: `${rowId}-${patternLabel}`,
							category: patternLabel,
							categoryKey: cat.key,
							roleGroup: rg.roleGroup,
							isGroupHeader: false,
							isExpandable: false,
							isExpanded: false,
							isPatternRow: true,
							isTotalRow: false,
							...Object.fromEntries(Array.from({ length: 14 }, (_, i) => [`d${i}`, pattern.hours[i] ?? 0])),
						} as HoursSummaryRow);
					}
				}
			}
		}
		return result;
	}, [data, expandedGroups, t, isActual]);

	const expandableRowIds = useMemo(() => new Set(rows.filter((r) => r.isExpandable).map((r) => r.id)), [rows]);

	const handleRowClick = useCallback(
		(params: { id: string | number }) => {
			const id = params.id as string;
			if (expandableRowIds.has(id)) {
				toggleGroupExpand(id);
			}
		},
		[expandableRowIds, toggleGroupExpand],
	);

	const getRowHeight = useCallback(({ model }: { model: unknown }) => {
		const row = model as unknown as HoursSummaryRow;
		return row?.isGroupHeader ? 40 : undefined;
	}, []);

	const getRowClassName = useCallback((params: { row: HoursSummaryRow }) => {
		if (params.row.isGroupHeader) return "group-header-row";
		if (params.row.isTotalRow) return "total-row";
		if (params.row.isPatternRow) return "pattern-row";
		return "";
	}, []);

	const dayColumns: GridColDef<HoursSummaryRow>[] = useMemo(() => {
		return Array.from({ length: 14 }, (_, i) => {
			const dayName = DAY_NAMES[i % 7];
			return {
				field: `d${i}`,
				headerName: dayName,
				flex: 1,
				minWidth: 65,
				align: "center" as const,
				headerAlign: "center" as const,
				sortable: false,
				renderCell: (params: GridRenderCellParams<HoursSummaryRow>) => {
					if (params.row.isGroupHeader) return null;
					const value = params.value as number;
					if (value == null || value === 0)
						return (
							<Typography
								variant="body2"
								sx={{
									color: "text.disabled",
								}}
							>
								-
							</Typography>
						);
					const color = getCategoryColor(params.row.categoryKey);
					return (
						<Typography
							variant="body2"
							color={color}
							sx={{
								fontWeight: params.row.isTotalRow ? 700 : 400,
							}}
						>
							{value}
						</Typography>
					);
				},
			} as GridColDef<HoursSummaryRow>;
		});
	}, []);

	const columns: GridColDef<HoursSummaryRow>[] = useMemo(
		() => [
			{
				field: "category",
				headerName: t("hours.summary.category"),
				minWidth: 200,
				sortable: false,
				renderCell: (params: GridRenderCellParams<HoursSummaryRow>) => {
					const row = params.row;
					if (row.isGroupHeader) {
						return (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 0.5,
								}}
							>
								<IconButton size="small" sx={{ p: 0 }} tabIndex={-1}>
									{row.isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
								</IconButton>
								<Typography
									variant="subtitle2"
									sx={{
										fontWeight: 700,
									}}
								>
									{row.category}
								</Typography>
							</Box>
						);
					}
					return (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
								pl: row.isPatternRow ? 4 : 2,
							}}
						>
							{row.isExpandable && (
								<IconButton size="small" sx={{ p: 0 }} tabIndex={-1}>
									{row.isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
								</IconButton>
							)}
							<Typography
								variant="body2"
								color={getCategoryColor(row.categoryKey)}
								sx={[
									{
										fontWeight: row.isTotalRow ? 700 : 400,
									},
									row.isExpandable ? { textDecoration: "underline dotted", textUnderlineOffset: 3 } : {},
								]}
							>
								{row.category}
							</Typography>
						</Box>
					);
				},
			},
			...dayColumns,
		],
		[t, dayColumns],
	);

	const columnGroupingModel: GridColumnGroupingModel = useMemo(
		() => [
			{
				groupId: "category-spacer",
				headerName: "",
				children: [{ field: "category" }],
			},
			{
				groupId: "week1",
				headerName: `${t("hours.summary.week")} 1`,
				headerAlign: "center" as const,
				children: Array.from({ length: 7 }, (_, i) => ({ field: `d${i}` })),
			},
			{
				groupId: "week2",
				headerName: `${t("hours.summary.week")} 2`,
				headerAlign: "center" as const,
				children: Array.from({ length: 7 }, (_, i) => ({ field: `d${i + 7}` })),
			},
		],
		[t],
	);

	if (isLoading) {
		return (
			<CommonDashboardBox sx={{ width: "100%" }}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 1,
						py: 1,
					}}
				>
					{Array.from({ length: 4 }, (_, i) => (
						<Box
							key={i.toString()}
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								px: 2,
								py: 1,
								bgcolor: HOURS_TABLE_TINTS.GROUP_HEADER_BG,
								borderRadius: "4px",
							}}
						>
							<Skeleton variant="text" width="25%" height={28} />
							{Array.from({ length: 14 }, (_, j) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton elements
								<Skeleton key={j} variant="text" width="5%" height={20} />
							))}
						</Box>
					))}
				</Box>
			</CommonDashboardBox>
		);
	}

	if (rows.length === 0) {
		return (
			<CommonDashboardBox sx={{ width: "100%" }}>
				<Typography
					variant="body2"
					sx={{
						color: "text.secondary",
						textAlign: "center",
						py: 4,
					}}
				>
					{t("hours.summary.no.data")}
				</Typography>
			</CommonDashboardBox>
		);
	}

	return (
		<CommonDashboardBox sx={{ width: "100%", overflow: "hidden" }}>
			<DataGrid
				rows={rows}
				columns={columns}
				columnGroupingModel={columnGroupingModel}
				onRowClick={handleRowClick}
				getRowHeight={getRowHeight}
				getRowClassName={getRowClassName}
				density="standard"
				disableColumnMenu
				disableColumnFilter
				hideFooter
				rowSelection={false}
				sx={GRID_SX}
				data-testid="hours-summary-table"
			/>
		</CommonDashboardBox>
	);
};

function getPatterns(rg: HoursSummaryRoleGroup, categoryKey: CategoryKey): HoursSummaryShiftPattern[] {
	switch (categoryKey) {
		case "weeklyOt":
			return rg.weeklyOtPatterns;
		case "dailyOt":
			return rg.dailyOtPatterns;
		case "shiftOverrun":
			return rg.shiftOverrunPatterns;
		case "agency":
			return rg.agencyPatterns;
		default:
			return [];
	}
}
